import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';
import SubtitlesHelper from '../../../../utils/SubtitlesHelper';
import { useUserContext } from '../../../../context/UserContext';
import ResourceHelper from '../../../../utils/ResourceHelper';
import { useVoiceoverContext } from '../../../../context/VoiceoverContext';
import { generateTextPanelsArray, generateTransitionsArray } from '../../../../utils/SimulatedItemsUtility';
import { timeStampToMs } from '../../../../utils/TimeUtil';
import RecordClipTrigger from '../../../../components/RecordClipTrigger/RecordClipTrigger';
import styles from './VideoDetailsPlayer.module.scss';
import { isVideoPathIsHls } from '../../../../utils/videoUtils';
import VideoPlayer from '../../../../routes/newVideo/VideoPlayer/VideoPlayer';
// import useBackgroundMusic from '../../../Editor/hooks/useBackgroundMusic';
import useBackgroundMusic from '../../../Editor/hooks/useBackgroundMusic';
import { useVideoProgressContext } from '../../../../context/VideoProgressContext';
import { EDITED } from '../../const';

const VideoDetailsPlayer = ({
    src,
    isSharedPage = false,
    video,
    hasHls,
    isVideoProcessing = false,
    miniRightSidebar,
    isMobileView,
    handleEditClicked,
    isExternallyEdited,
    setIsFirstPlayed,
    videoVersion = null,
    videoConfig,
    ...props
}) => {

    const [play, setPlay] = useState(false);

    const { t } = useTranslation();

    const { appConfig } = useUserContext();

    const { isVoiceoverOpened, isVoiceoverRecording, isVoiceoverCountdown, } = useVoiceoverContext();
    const { videoProgress } = useVideoProgressContext();
    const { currentPart, videoParts } = useVoiceoverContext();

    const withBackgroundMusic = useMemo(() => !isSharedPage && isVoiceoverOpened, [isVoiceoverOpened]);

    const { simulatedTextPanel, simulatedLogo, simulatedSubtitles } = useMemo(() => {
        let simulatedLogo = false;
        let simulatedTextPanel = false;
        let simulatedSubtitles = false;
        const { simulatedResources } = video;
        if (simulatedResources?.includes('*')) {
            simulatedTextPanel = true;
            simulatedLogo = true;
            simulatedSubtitles = true;
        } else {
            simulatedTextPanel = simulatedResources?.includes('textPanel');
            simulatedLogo = simulatedResources?.includes('logo');
            simulatedSubtitles = simulatedResources?.includes('subtitles');
        }
        if (video.scenes.filter(scene => Boolean(scene?.isAdaptor)).length > 0) {
            simulatedTextPanel = false; // No text panels for advanced editor videos
            simulatedLogo = false; // No logo for advanced editor videos
        }
        return { simulatedTextPanel, simulatedLogo, simulatedSubtitles };
    }, [video]);

    const handleSubtitles = (subtitlesMeta, visibleSubtitleLanguages) => {
        const subsObject = {};
        subtitlesMeta
            ?.filter(x => x?.visible)
            .forEach(item => {
                if (visibleSubtitleLanguages.some(elem => elem.language === item.language) || (item.original && item.visible)) {
                    subsObject[item.language] = { src: item.vttPath, original: Boolean(item.original) };
                }
            });
        return subsObject;
    };

    const extractSceneVideo = (videoDetails, includeTrimming) => {
        return videoDetails?.scenes?.map(({ orientation, dimensions, trimStart, trimEnd }) => ({
            orientation,
            dimensions,
            ...(includeTrimming ? { trimStart: timeStampToMs(trimStart), trimEnd: timeStampToMs(trimEnd) } : {}),
        }));
    };

    const { logo = null, textPanels = null, subtitles = null, subtitlesSettings = null, transitionsArray = null, sceneData = null } = useMemo(() => {
        if (!video || !videoConfig) {
            return {};
        }
        const sortedScenes = video?.scenes?.sort((a, b) => a.sort - b.sort);
        const isVideoPathHls = isVideoPathIsHls(video.id, video.videoPath);

        let logo = null;
        if (simulatedLogo) {
            logo = videoConfig.logo;
        }
        let textPanels = [];
        if (simulatedTextPanel) {
            textPanels = generateTextPanelsArray(sortedScenes, video.introMeta?.duration);
            // Only one scene in video, hls is not ready yet, original file will be played,
            // make sure text panel corresponds this scene trm duration
            if (!isVideoPathHls && video.scenes.length === 1 && textPanels.length) {
                const trimStart = timeStampToMs(video.scenes[0].trimStart);
                textPanels[0].start += trimStart;
                textPanels[0].end += trimStart;
            }
        }
        let transitionsArray = [];
        if (video.transitions?.length > 0) {
            transitionsArray = generateTransitionsArray(sortedScenes, video.transitions, video.introMeta?.duration);
        }

        const subtitles = simulatedSubtitles ? handleSubtitles(video.subtitlesMeta, video.visibleSubtitleLanguages) : null;

        let resolvedSubtitlesSettings = videoConfig?.subtitlesSettings;
        const subtitlesSettings = SubtitlesHelper.appendMarginYForOrientation(resolvedSubtitlesSettings, video.orientation);

        const sceneData = extractSceneVideo(
            video,
            !isVideoPathHls && !video?.hasIntro && !video?.hasOutro && video?.scenes?.length === 1
        );

        return { logo, textPanels, transitionsArray, subtitles, subtitlesSettings, sceneData };
    }, [video, videoConfig]);

    const videoUrl = useMemo(() => {
        if (isVoiceoverOpened) {
            let hlsVoiceoverAudio = ResourceHelper.getResourceByLabel(video, 'originalAudio')?.url ||
                ResourceHelper.getResourceByLabel(video, 'hls')?.url ||
                video.videoPath

            return hlsVoiceoverAudio;
        }

        return src;
    }, [isVoiceoverOpened, src]);

    const { handleStartBackgroundMusic, handleStopBackgroundMusic, onSeekCallback, onVolumeChange, onPlaybackRateChange } = useBackgroundMusic({
        scenes: videoParts,
        currentScene: currentPart,
        isVideoPlaying: play,
        playedProgress: (currentPart ? (videoProgress?.milliseconds - currentPart?.start) : videoProgress?.milliseconds) / 1000,
        startSimulation: !isSharedPage && isVoiceoverOpened,
        blockPlaying: (isVoiceoverRecording || isVoiceoverCountdown)
    });

    const handlePlaybackRateChange = (value) => {
        onPlaybackRateChange(value);
    }

    const handleVolumeChange = (value) => {
        onVolumeChange(value);
    }

    const onPlayChanged = (value) => {
        setPlay(value);
        setIsFirstPlayed && value && setIsFirstPlayed(true);
    }

    const handleSeekTo = (seekTime) => {
        const seekTimeInMs = seekTime * 1000;
        const seekToScene = videoParts.find(({ start, trimmedDuration }) => seekTimeInMs >= start && seekTimeInMs < start + trimmedDuration) ?? null;
        seekToScene && onSeekCallback((seekTimeInMs - seekToScene.start) / 1000);
    }


    return <div className={styles.videoPlayerComponent}>
        <VideoPlayer
            uuid={video.id}
            originalURL={videoUrl}
            thumbnailURL={isExternallyEdited ? null : video.thumbnail}
            scenes={sceneData}
            overlay
            status={video.status}
            hasHls={hasHls}
            videoDuration={videoVersion !== EDITED ? video.duration : null}
            processing={!video?.videoPath && isVideoProcessing}
            subtitles={subtitles}
            subtitlesSettings={subtitlesSettings}
            textPanels={textPanels}
            transitionsArray={transitionsArray}
            disableDownload={video.source === 'web' && video?.scenes?.length > 1 && hasHls}
            isDraft={video.draft}
            miniRightSidebar={miniRightSidebar}
            isSharedPage={isSharedPage}
            handleTogglePlaying={onPlayChanged}
            bufferingDelay={200}
            logo={logo}
            videoConfig={videoConfig}
            videoVolumeWithMusic={Number.isFinite(currentPart?.videoVolume) ? currentPart?.videoVolume : 1}
            {...(withBackgroundMusic ? {
                handlePlaybackRateChange,
                handleStartBackgroundMusic,
                handleStopBackgroundMusic,
                onSeekCallback: handleSeekTo,
                handleVolumeUpdated: handleVolumeChange,
                directPlayerVolume: currentPart?.videoVolume ?? null
            } : {})}
            {...(isExternallyEdited ? {} : { intro: video.introMeta, outro: video.outroMeta })}
            {...props}
        />
        {!isSharedPage && video.draft ? (
            <RecordClipTrigger
                actions={isMobileView ? [] : [{ label: t('my_video.unfinished_video.cta'), onClick: handleEditClicked }]}
                message={t('my_video.unfinished_video')}
            />
        ) : null}
    </div>
}

export default VideoDetailsPlayer;